/**
 * Tab Manager for ensuring a single active order tab in configurator applications.
 *
 * @description Prevents duplicate tab actions by tracking tab activity across browser tabs using localStorage.
 * Automatically enables/disables buttons based on whether the current tab is the "main" tab.
 * Dispatches custom events to allow external listeners to respond to tab state changes.
 *
 * Usage:
 *   // After DOM is ready:
 *   detectTabMgr.init('orderBtn');         // For a single button by ID
 *   detectTabMgr.init('.orderBtn');        // For multiple buttons by class
 *   detectTabMgr.init(['#btn1', '.btn2']); // For multiple selectors
 *
 *   // Buttons added dynamically via AJAX or DOM changes will be detected and managed automatically.
 *
 * Key Methods:
 *   - init(...selectors): Initializes the tab manager with button selectors
 *   - updateOrderBtns(): Refreshes the list of managed buttons
 *   - addListeners(): Adds click and lifecycle listeners to managed buttons and window
 *   - isMainTab(): Returns true if this tab is the main tab
 *   - checkTab(): Validates tab status and updates button states
 *   - setActiveTab(): Marks this tab as active in localStorage
 *   - releaseLock(): Cleans up tab data on unload
 *   - setButtonState(enabled): Enables/disables buttons based on tab status
 *
 * Events dispatched:
 *   - ecux.detectTabMgr.tabAdded
 *   - ecux.detectTabMgr.tabRemoved
 *   - ecux.detectTabMgr.tabActive
 *   - ecux.detectTabMgr.mainTab
 *   - ecux.detectTabMgr.duplicateTab
 *   - ecux.detectTabMgr.tabListChanged
 *   - ecux.detectTabMgr.tabStateSync
 *   - ecux.detectTabMgr.mainTabChanged
 */
const detectTabMgr = {
    lastMainTabId: null,
    tabId: "ecux_dtm_CONFIGURATOR-" + Date.now() + "-" + Math.random(),
    orderKey: "ecux_dtm_activeOrderTab",
    tabListKey: "ecux_dtm_activeOrderTabList",
    mainTabKey: "ecux_dtm_mainOrderTabId",
    orderBtnSelector: null,
    orderBtns: [],
    tabCreated: Date.now(),
    activityIntervalId: null,
    TAB_ACTIVITY_REPORT_INTERVAL: 30000,

    getTabList: function () {
        let list = [];
        try {
            list = JSON.parse(localStorage.getItem(this.tabListKey)) || [];
        } catch (e) { }
        return list;
    },

    setTabList: function (list) {
        localStorage.setItem(this.tabListKey, JSON.stringify(list));
    },

    getCurrentTabId: function () {
        return this.tabId;
    },

    dispatchEvent: function (eventType, detail) {
        const event = new CustomEvent(`ecux.detectTabMgr.${eventType}`, { detail });
        window.dispatchEvent(event);
        if (window.ecux.debug.isDebugEnabled()) {
            window.console.table(
                '[ECUX]',
                '[detectTabMgr]',
                `[ecux.detectTabMgr.${eventType}]`,
                detail
            );
        }
    },

    // Helper to dispatch tab state sync event
    dispatchTabStateSync: function () {
        const isMain = this.isMainTab();
        this.dispatchEvent('tabStateSync', {
            tabId: this.tabId,
            tabList: this.getTabList(),
            isMainTab: isMain
        });
    },

    reportTabActivity: function () {
        let list = this.getTabList();
        const now = Date.now();
        let found = false;
        list = list.map(tab => {
            if (tab.id === this.tabId) {
                found = true;
                // Always preserve the original created timestamp
                return { ...tab, lastActive: now, created: tab.created || this.tabCreated };
            }
            return tab;
        });
        if (!found) {
            list.push({ id: this.tabId, created: this.tabCreated, lastActive: now });
        }
        this.setTabList(list);
        this.dispatchEvent('tabActive', { tabId: this.tabId, tabList: list });
        this.dispatchTabStateSync();
    },

    cleanTabList: function () {
        // Remove tabs older than 30min or inactive for TAB_ACTIVITY_REPORT_INTERVAL
        const now = Date.now();
        let list = this.getTabList();
        list = list.filter(tab =>
            (now - tab.created < 30 * 60 * 1000) && (now - (tab.lastActive || tab.created) < this.TAB_ACTIVITY_REPORT_INTERVAL)
        );
        this.setTabList(list);
        return list;
    },

    addTab: function () {
        let list = this.cleanTabList();
        list.push({ id: this.tabId, created: this.tabCreated });
        this.setTabList(list);
        if (!localStorage.getItem(this.mainTabKey)){
            localStorage.setItem(this.mainTabKey, this.tabId);
        };
        this.dispatchEvent('tabAdded', { tabId: this.tabId, tabList: list });
        this.dispatchTabStateSync();
    },

    removeTab: function () {
        let list = this.getTabList();
        list = list.filter(tab => tab.id !== this.tabId);
        this.setTabList(list);
        this.dispatchEvent('tabRemoved', { tabId: this.tabId, tabList: list });
        this.dispatchTabStateSync();
    },

    getMainTabId: function () {
        const list = this.cleanTabList();
        return localStorage.getItem(this.mainTabKey);
    },

    isMainTab: function () {
        const mainId = this.getMainTabId();
        return !mainId || mainId === this.tabId;
    },

    init: function (selectorsArray) {
        this.orderBtnSelector = selectorsArray;
        this.addTab();
        this.updateOrderBtns();
        this.checkTab();
        this.addListeners();
        this.startTabActivityReporting();
    },

    updateOrderBtns: function () {
        this.orderBtns = [];
        if (Array.isArray(this.orderBtnSelector)) {
            this.orderBtnSelector.forEach(sel => {
                if (sel.startsWith("#") || sel.startsWith(".")) {
                    this.orderBtns = this.orderBtns.concat(Array.from(document.querySelectorAll(sel)));
                } else {
                    const btn = document.getElementById(sel);
                    if (btn) this.orderBtns.push(btn);
                }
            });
        } else if (typeof this.orderBtnSelector === "string") {
            if (this.orderBtnSelector.startsWith("#") || this.orderBtnSelector.startsWith(".")) {
                this.orderBtns = Array.from(document.querySelectorAll(this.orderBtnSelector));
            } else {
                const btn = document.getElementById(this.orderBtnSelector);
                this.orderBtns = btn ? [btn] : [];
            }
        }
    },

    addListeners: function () {
        this.orderBtns.forEach(btn => {
            btn.addEventListener("click", (e) => {
                if (e.target.classList.contains("duplicate-tab-blocker")) {
                    this.checkTab(); // Only check, do not set active tab
                } else {
                    this.setActiveTab();
                }
            });
        });
        window.addEventListener("beforeunload", () => {
            this.releaseLock();
        });
        window.addEventListener('pagehide', () => {
            this.releaseLock();
        });
        window.addEventListener("storage", () => {
            this.checkTab();
        });

        // Add click listener for duplicate-tab-blocker class (for non-button elements)
        document.body.addEventListener("click", (e) => {
            if (e.target.classList.contains("duplicate-tab-blocker")) {
                this.checkTab();
            }
        });
    },

    checkTab: function () {
        const currentMainTabId = this.getMainTabId();
        const beforeList = this.getTabList();
        this.cleanTabList();
        const afterList = this.getTabList();
        if (this.isMainTab()) {
            this.setButtonState(true);
            this.dispatchEvent('mainTab', { tabId: this.tabId, tabList: afterList });
        } else {
            this.setButtonState(false);
            this.dispatchEvent('duplicateTab', { tabId: this.tabId, tabList: afterList });
        }
        if (beforeList.length !== afterList.length) {
            this.dispatchEvent('tabListChanged', { tabId: this.tabId, tabList: afterList });
        }
        if (this.lastMainTabId && this.lastMainTabId !== currentMainTabId) {
            let tabChangedData = { lastTabId: this.lastMainTabId, currentTabId: currentMainTabId, tabList: afterList };
            this.dispatchEvent('mainTabChanged', tabChangedData);
        }

        this.lastMainTabId = currentMainTabId;
        this.dispatchTabStateSync();
    },

    setActiveTab: function () {
        localStorage.setItem(this.orderKey, this.tabId);
    },

    releaseLock: function () {
        this.removeTab();
        this.stopTabActivityReporting();
        if (localStorage.getItem(this.mainTabKey) === this.tabId) {
            localStorage.removeItem(this.mainTabKey);
        };

        if (this.getTabList().length === 0) {
            localStorage.removeItem(this.orderKey);
            localStorage.removeItem(this.tabListKey);
            localStorage.removeItem(this.mainTabKey);
        }
    },

    setButtonState: function (enabled) {
        this.updateOrderBtns();
        this.orderBtns.forEach(btn => {
            if (enabled) {
                btn.disabled = false;
                btn.classList.remove("disabled");
                btn.classList.remove("duplicate-tab-blocker");


            } else {
                if (btn.id !== 'configurator-footer-actions-summary' && btn.id !== 'configurator-footer-actions-summary-m') {
                    btn.disabled = true;
                    btn.classList.add("disabled");
                } else {
                    btn.disabled = false;
                }

                btn.classList.add("duplicate-tab-blocker");
            }

            if (window.ecux.debug.isDebugEnabled()) {
                if (enabled) {
                    window.console.table(
                        '[ECUX]',
                        '[detectTabMgr]',
                        '[CTA block released]',
                        btn.id
                    );
                } else {
                    window.console.table(
                        '[ECUX]',
                        '[detectTabMgr]',
                        '[CTA blocked]',
                        btn.id
                    );
                }
            }
        });
    },

    startTabActivityReporting: function () {
        this.reportTabActivity();
        if (this.activityIntervalId) clearInterval(this.activityIntervalId);
        this.activityIntervalId = setInterval(() => {
            this.reportTabActivity();
        }, this.TAB_ACTIVITY_REPORT_INTERVAL);
    },

    stopTabActivityReporting: function () {
        if (this.activityIntervalId) clearInterval(this.activityIntervalId);
        this.activityIntervalId = null;
    },
};

module.exports = detectTabMgr;
